import { Field, YesObject } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useReducer, useState } from 'react'

type UseEditableFieldsProps = {
  fields: Pick<Field, 'column_json'>[]
  yesObject?: YesObject
  onSave: (values: YesObject) => Promise<void>
}

const pickRelatedFieldValues = (
  fields: Pick<Field, 'column_json'>[],
  yesObject?: YesObject
): YesObject | undefined => {
  if (!yesObject) {
    return yesObject
  }

  return Object.fromEntries(
    fields.flatMap((field) =>
      field.column_json in yesObject
        ? [[field.column_json, yesObject[field.column_json]]]
        : []
    )
  ) as YesObject
}

export default function useEditableFields({
  fields,
  yesObject,
  onSave
}: UseEditableFieldsProps) {
  const [fieldValues, setFieldValues] = useState(() =>
    pickRelatedFieldValues(fields, yesObject)
  )
  const [isEditing, toggleEditing] = useReducer(
    (state: boolean) => !state,
    false
  )

  const onChange = useCallback(
    (fieldColumn: string, value: YesObject[string]) => {
      setFieldValues(
        (previousFieldValues) =>
          ({
            ...previousFieldValues,
            [fieldColumn]: value
          }) as YesObject
      )
    },
    []
  )

  const onToggleEditing = (cancel?: boolean) => {
    if (cancel) setFieldValues(pickRelatedFieldValues(fields, yesObject))

    if (isEditing && fieldValues && !cancel) {
      onSave(fieldValues)
    }

    toggleEditing()
  }

  const save = useCallback(async () => {
    if (fieldValues) await onSave(fieldValues)
  }, [fieldValues, onSave])

  const changeAndSave = useCallback(
    async (fieldColumn: string, value: YesObject[string]) => {
      const newFieldValues = {
        ...fieldValues,
        [fieldColumn]: value
      } as YesObject

      setFieldValues(newFieldValues)
      await onSave(newFieldValues)
    },
    [fieldValues, onSave]
  )

  useEffect(() => {
    setFieldValues(pickRelatedFieldValues(fields, yesObject))
  }, [fields, yesObject])

  return {
    fieldValues,
    isEditing,
    onChange,
    onToggleEditing,
    save,
    changeAndSave
  }
}
